'use client';

import type { OleAIInstance, OleChatbotTheme, OleChatbotThemeMode, OleDeeplinkPayload } from '@ole-ai/embed-types';
import Script from 'next/script';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from 'next-themes';
import { useAccountInfo } from '@/hooks/query/account';
import { useAppearanceStore } from '@/store/appearance';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useLocale } from 'next-intl';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { useAuthToken } from '@/store/auth';
import { useApiBackendInfo } from '@/hooks/query/use-api-backend-info';
import { authService } from '@/services/auth.service';
import { ViewEnum } from '@/types';
import { getEnvironmentFromOneDomain } from '@/services/onedomain.service';
import { useRouter } from '@/navigation';
import { GetMarket } from '@/utils/common-util';

const isProduction = getEnvironmentFromOneDomain() === 'PROD';
const EMBED_SCRIPT_SRC = isProduction
	? 'https://www.ole-chat.com/js/ole-sdk.js'
	: 'https://staging.ole-chat.com/js/ole-sdk.js';
const CHATBOT_INSTANCE_ID = 'ole-ai';

interface AuthIdentity {
	apiDomain: string;
	siteId: string;
	vendorMemberId: string;
	backendToken: string;
}

export const aIChatToggleRef: { current: (() => Promise<void>) | null } = { current: null };

export const AIChat = () => {
	const router = useRouter();
	const { user } = useAccountInfo();
	const { theme } = useTheme();
	const view = useAppearanceStore(state => state.view);
	const { SiteConfigsData } = useGetSiteConfigs();
	const locale = useLocale();
	const apiBackendInfo = useApiBackendInfo();
	const authToken = useAuthToken();
	const { enableAIChat } = useLicSettings();

	const instanceRef = useRef<OleAIInstance | null>(null);
	const authIdentityRef = useRef<AuthIdentity | null>(null);
	const [scriptReady, setScriptReady] = useState(false);

	useEffect(() => {
		if (window.oleai) setScriptReady(true);
	}, []);

	const embedTheme: OleChatbotTheme = view === ViewEnum.mars ? 'mars' : 'galaxy';
	const themeMode: OleChatbotThemeMode = theme === 'dark' ? 'dark' : 'light';

	const fetchNeuronToken = useCallback((identity: AuthIdentity) => {
		return new Promise<string | undefined>((resolve, reject) => {
			authService
				.fetchNeuronToken({
					apiDomain: identity.apiDomain,
					siteId: identity.siteId,
					vendorMemberId: identity.vendorMemberId,
					token: identity.backendToken,
				})
				.subscribe({
					next: async (result) => {
						const data = await result;
						if (data.ErrorCode !== 0) {
							resolve(undefined);
							return;
						}
						resolve(data.Data ?? undefined);
					},
					error: reject,
				});
		});
	}, []);

	const handleRefetchToken = useCallback(async () => {
		const identity = authIdentityRef.current;
		if (!identity) throw new Error('Not logged in');

		const token = await fetchNeuronToken(identity);
		if (!token) throw new Error('Failed to fetch neuron token');
		return token;
	}, [fetchNeuronToken]);

	const handleDeeplink = useCallback((payload: OleDeeplinkPayload) => {
		if (payload.type === 'url') {
			router.push(payload.url);
			return;
		}
		const { matchId, sportId, leagueId, globalShowTime } = payload;
		const path = `/sports/${sportId}/${GetMarket(globalShowTime)}/${leagueId}/${matchId}`;
		router.push(path);
	}, [router]);

	useEffect(() => {
		if (SiteConfigsData?.GetMessageID && user?.VenderMemberId && apiBackendInfo.isReady) {
			authIdentityRef.current = {
				apiDomain: apiBackendInfo.url,
				siteId: SiteConfigsData.GetMessageID,
				vendorMemberId: user.VenderMemberId,
				backendToken: apiBackendInfo.token,
			};
		} else {
			authIdentityRef.current = null;
		}
	}, [
		SiteConfigsData?.GetMessageID,
		user?.VenderMemberId,
		apiBackendInfo.url,
		apiBackendInfo.token,
	]);

	useEffect(() => {
		return () => {
			window.oleai?.destroy(CHATBOT_INSTANCE_ID);
			instanceRef.current = null;
		};
	}, [SiteConfigsData?.GetMessageID, user?.VenderMemberId]);

	useEffect(() => {
		if (!instanceRef.current) return;
		instanceRef.current.updateConfig({ themeMode });
	}, [themeMode]);

	useEffect(() => {
		if (!instanceRef.current) return;
		instanceRef.current.updateConfig({ language: locale, theme: embedTheme });
	}, [locale, embedTheme]);

	const handleToggleChat = async () => {
		if (instanceRef.current?.isOpen()) {
			instanceRef.current.close();
			return;
		}

		const identity = authIdentityRef.current;
		if (!identity || !scriptReady || !window.oleai) return;

		try {
			if (instanceRef.current) {
				await instanceRef.current.open();
				return;
			}

			const token = await fetchNeuronToken(identity);
			if (!token) throw new Error('Failed to fetch neuron token');

			instanceRef.current = window.oleai.create(CHATBOT_INSTANCE_ID, {
				loginToken: token,
				language: locale,
				theme: embedTheme,
				themeMode,
				draggableWindow: true,
				isDev: !isProduction,
				showButton: false, // 不使用sdk 的button
				onDeeplink: handleDeeplink,
				onRefetchToken: handleRefetchToken,
			});
			await instanceRef.current.open();
		} catch (err) {
			console.error('[oleai] Failed to open chat', err);
			window.oleai?.destroy(CHATBOT_INSTANCE_ID);
			instanceRef.current = null;
		}
	};

	aIChatToggleRef.current = handleToggleChat;

	if (!user?.ID || !authToken || !enableAIChat) return null;

	return (
		<Script
			src={EMBED_SCRIPT_SRC}
			strategy="afterInteractive"
			onLoad={() => setScriptReady(true)}
			onError={() => console.error('[oleai] Failed to load embed script')}
		/>
	);
};
